import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { universalBlogEditorPath } from '@/utils/editorRoutes';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from '@/components/ui/data-table';
import type { UsagePiece } from '@/services/usagePiecesService';

function prettyModel(model: string | null): string {
  if (!model) return '—';
  return model.includes('/') ? model.slice(model.lastIndexOf('/') + 1) : model;
}

const SORT_ACCESSORS: SortAccessors<UsagePiece> = {
  title: (r) => r.title.toLowerCase(),
  template: (r) => r.template ?? '',
  words: (r) => r.word_count,
  attempts: (r) => r.attempts,
  model: (r) => prettyModel(r.model),
  quality: (r) => r.quality_score,
  credits: (r) => r.credits,
};

const RESIZE_DEFAULTS: Record<string, number> = {
  title: 300,
  template: 150,
  words: 90,
  attempts: 90,
  model: 170,
  quality: 100,
  credits: 150,
};

function usd(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function uniqueOptions(values: (string | null)[]): { value: string; label: string }[] {
  const set = new Set<string>();
  for (const v of values) if (v) set.add(v);
  return [...set].sort().map((v) => ({ value: v, label: v }));
}

function qualityColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 80) return 'text-pace-good-dark';
  if (score >= 60) return 'text-pace-warn-dark';
  return 'text-pace-over-dark';
}

export function UsagePiecesTable({
  pieces,
  creditDollarValue = 0.1,
}: {
  pieces: UsagePiece[];
  /** USD per credit (published rate) — for the "$" alongside credits. */
  creditDollarValue?: number;
}) {
  const navigate = useNavigate();
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<UsagePiece>('credits', 'desc');
  const { widths, startResize } = useColumnResize(RESIZE_DEFAULTS);
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');

  const filterColumns: FilterColumn<UsagePiece>[] = useMemo(
    () => [
      { key: 'title', label: 'Title', type: 'text', accessor: (r) => r.title },
      {
        key: 'template',
        label: 'Template',
        type: 'enum',
        accessor: (r) => r.template ?? '—',
        options: uniqueOptions(pieces.map((p) => p.template)),
      },
      {
        key: 'model',
        label: 'Model',
        type: 'enum',
        accessor: (r) => prettyModel(r.model),
        options: uniqueOptions(pieces.map((p) => prettyModel(p.model))),
      },
      {
        key: 'published',
        label: 'Published',
        type: 'enum',
        accessor: (r) => (r.published ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
      { key: 'words', label: 'Words', type: 'number', accessor: (r) => r.word_count },
      { key: 'attempts', label: 'Attempts', type: 'number', accessor: (r) => r.attempts },
      { key: 'quality', label: 'Quality', type: 'number', accessor: (r) => r.quality_score },
      { key: 'credits', label: 'Cost (credits)', type: 'number', accessor: (r) => r.credits },
    ],
    [pieces],
  );

  const rows = useMemo(() => {
    const filtered = applyFilterRules(pieces, rules, filterColumns, conjunction);
    return sortRows(filtered, SORT_ACCESSORS);
  }, [pieces, rules, filterColumns, conjunction, sortRows]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(rows.length, {
    defaultPageSize: 20,
  });
  const pageRows = slice(rows);

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  return (
    <div className="space-y-3">
      <FilterBuilder
        columns={filterColumns}
        rules={rules}
        conjunction={conjunction}
        onRulesChange={setRules}
        onConjunctionChange={setConjunction}
      />

      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {th('title', 'Title')}
              {th('template', 'Template')}
              {th('words', 'Words', 'text-right')}
              {th('attempts', 'Attempts', 'text-right')}
              {th('model', 'Model')}
              {th('quality', 'Quality', 'text-right')}
              {th('credits', 'Cost', 'text-right')}
              <TableHead style={{ width: 56 }} className="text-center">
                Link
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                  No pieces match.
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((p, i) => {
                const clickable = Boolean(p.content_id);
                return (
                  <TableRow
                    key={`${p.content_id ?? p.title}-${i}`}
                    onClick={() =>
                      clickable && navigate(universalBlogEditorPath(p.content_id as string))
                    }
                    className={clickable ? 'cursor-pointer' : undefined}
                  >
                    <TableCell className="max-w-0 truncate font-medium text-foreground" title={p.title}>
                      {p.title}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{p.template ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {p.word_count !== null ? p.word_count.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {p.attempts}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{prettyModel(p.model)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${qualityColor(p.quality_score)}`}>
                      {p.quality_score ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums text-foreground">
                      {p.credits.toLocaleString()} cr{' '}
                      <span className="text-muted-foreground">
                        ({usd(p.credits * creditDollarValue)})
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {p.published && p.wordpress_url ? (
                        <a
                          href={p.wordpress_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex text-coral hover:text-coral-hover"
                          title="Open published post"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={rows.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
